import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import { matches, articles, highlights, categories } from '@/mocks/sportsData';
import type { Match, Article, Highlight } from '@/mocks/sportsData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const match: Match | undefined = matches.find((m) => m.id === id);
  const article: Article | undefined = articles.find((a) => a.id === id);

  const relatedArticles: Article[] = articles.filter((a) => a.id !== id).slice(0, 3);

  if (match) return <MatchDetail match={match} relatedArticles={relatedArticles} />;
  if (article) return <ArticleDetail article={article} relatedArticles={relatedArticles} />;

  return (
    <>
      <SEO title="赛事详情 - 每日大赛" description="每日大赛在线观看赛事详情" />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-10 px-4 md:px-6 text-center">
          <div className="max-w-[600px] mx-auto">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">赛事未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">该赛事可能已下架或不存在</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}

function MatchDetail({ match, relatedArticles }: { match: Match; relatedArticles: Article[] }) {
  const category = categories.find((c) => c.slug === match.categorySlug);
  const matchHighlights: Highlight[] = highlights.filter((h) => h.matchId === match.id);
  const relatedMatches: Match[] = matches.filter((m) => m.categorySlug === match.categorySlug && m.id !== match.id).slice(0, 4);

  const pageTitle = `${match.teamA} vs ${match.teamB} - ${match.title} | 每日大赛在线观看`;
  const pageDesc = `${match.teamA}对阵${match.teamB}，${match.league}${match.title}，每日大赛在线观看为您带来高清${category?.name || '体育'}赛事直播。`;

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${match.teamA},${match.teamB},${match.league},${category?.name || '赛事'}直播,每日大赛在线观看`}
        canonicalUrl={`https://www.meiri-dasai.com/article/${match.id}`}
        ogType="article"
      />
      <SchemaOrg type="WebPage" data={{ name: pageTitle, url: `https://www.meiri-dasai.com/article/${match.id}`, isPartOf: { '@type': 'WebSite', name: '每日大赛在线观看' } }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [{ '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.meiri-dasai.com/' }, { '@type': 'ListItem', position: 2, name: category?.name || '赛事', item: `https://www.meiri-dasai.com/category/${match.categorySlug}` }, { '@type': 'ListItem', position: 3, name: pageTitle }] }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-4 px-4 md:px-6 bg-background-100">
          <div className="max-w-[960px] mx-auto">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category?.name || '赛事', to: `/category/${match.categorySlug}` }, { label: `${match.teamA} vs ${match.teamB}` }]} />
          </div>
        </div>

        <main>
          <article className="py-8 md:py-10 px-4 md:px-6">
            <div className="max-w-[960px] mx-auto">
              <div className="relative h-[320px] md:h-[440px] rounded-lg overflow-hidden mb-8">
                <img src={match.imageUrl} alt={`${match.teamA} vs ${match.teamB}`} className="w-full h-full object-cover" />
                {match.status === 'live' && (
                  <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500 text-white text-sm font-medium">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
                    直播中 {match.time}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">{match.league}</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">{category?.name}</span>
                <span className="text-xs text-foreground-500">{match.date}</span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">{match.teamA} vs {match.teamB}</h1>

              <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8">
                <div className="flex items-center justify-center gap-8 md:gap-16">
                  <div className="text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70">
                      <span className="text-lg md:text-xl font-heading font-bold text-primary-600">{match.teamA}</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900">{match.teamA}</span>
                  </div>
                  <div className="text-center">
                    {match.scoreA !== null ? (
                      <div>
                        <span className="text-4xl md:text-5xl font-heading font-bold text-primary-500">{match.scoreA} - {match.scoreB}</span>
                        <p className="text-sm text-foreground-500 mt-2">{match.time}</p>
                      </div>
                    ) : (
                      <div>
                        <span className="text-2xl md:text-3xl font-heading font-bold text-foreground-300">VS</span>
                        <p className="text-sm text-foreground-500 mt-2">{match.time}</p>
                      </div>
                    )}
                    {match.status === 'live' && (
                      <span className="inline-block mt-2 px-3 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">直播中</span>
                    )}
                    {match.status === 'upcoming' && (
                      <span className="inline-block mt-2 px-3 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">即将开始</span>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70">
                      <span className="text-lg md:text-xl font-heading font-bold text-primary-600">{match.teamB}</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900">{match.teamB}</span>
                  </div>
                </div>
              </div>

              <div className="prose prose-sm max-w-none mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">赛事信息</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {match.title}于{match.date}进行，由{match.teamA}对阵{match.teamB}。本场比赛是{match.league}的精彩对决，每日大赛在线观看为您带来高清直播体验。
                  {match.status === 'live' && '比赛正在进行中，当前比分为 ' + match.scoreA + '-' + match.scoreB + '，请持续关注每日大赛在线观看获取最新赛况。'}
                  {match.status === 'upcoming' && `比赛将于${match.time}开始，敬请锁定每日大赛在线观看，不错过任何精彩瞬间。`}
                  {match.status === 'finished' && `比赛已结束，最终比分 ${match.scoreA}-${match.scoreB}。您可以在每日大赛在线观看查看全场回放。`}
                </p>
              </div>

              {matchHighlights.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">赛事集锦</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {matchHighlights.map((hl) => (
                      <div key={hl.id} className="bg-background-100 rounded-lg overflow-hidden">
                        <div className="relative h-36 overflow-hidden">
                          <img src={hl.imageUrl} alt={hl.title} className="w-full h-full object-cover" />
                          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/65 text-white text-xs font-medium">
                            <i className="ri-play-fill mr-0.5"></i>{hl.duration}
                          </div>
                        </div>
                        <div className="p-3">
                          <p className="text-sm font-medium text-foreground-900">{hl.title}</p>
                          <p className="text-xs text-foreground-500 mt-1">{hl.views} 次观看</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>

          {/* Related matches */}
          {relatedMatches.length > 0 && (
            <section className="py-8 md:py-10 px-4 md:px-6 bg-background-100">
              <div className="max-w-[960px] mx-auto">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关赛事</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedMatches.map((rm) => (
                    <Link
                      key={rm.id}
                      to={`/article/${rm.id}`}
                      onClick={() => window.scrollTo(0, 0)}
                      className="flex items-center gap-4 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-16 h-12 flex-shrink-0 rounded overflow-hidden">
                        <img src={rm.imageUrl} alt={`${rm.teamA} vs ${rm.teamB}`} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate">{rm.teamA} vs {rm.teamB}</p>
                        <p className="text-xs text-foreground-500">{rm.league} · {rm.date}</p>
                      </div>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-right-s-line text-foreground-400"></i>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Related articles */}
          {relatedArticles.length > 0 && (
            <section className="py-8 md:py-10 px-4 md:px-6 bg-background-50">
              <div className="max-w-[960px] mx-auto">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关资讯</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      onClick={() => window.scrollTo(0, 0)}
                      className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img src={ra.imageUrl} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2">{ra.title}</h3>
                        <p className="text-xs text-foreground-500 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}

function ArticleDetail({ article, relatedArticles }: { article: Article; relatedArticles: Article[] }) {
  const category = categories.find((c) => c.slug === article.categorySlug);

  const pageTitle = `${article.title} | 每日大赛在线观看`;
  const pageDesc = article.excerpt;

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${article.tags.join(',')},每日大赛在线观看,${category?.name || '体育'}赛事资讯`}
        canonicalUrl={`https://www.meiri-dasai.com/article/${article.id}`}
        ogType="article"
      />
      <SchemaOrg type="Article" data={{ headline: article.title, description: article.excerpt, author: { '@type': 'Person', name: article.author }, datePublished: article.date, image: article.imageUrl }} />
      <SchemaOrg type="WebPage" data={{ name: pageTitle, url: `https://www.meiri-dasai.com/article/${article.id}`, isPartOf: { '@type': 'WebSite', name: '每日大赛在线观看' } }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [{ '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.meiri-dasai.com/' }, { '@type': 'ListItem', position: 2, name: category?.name || '赛事资讯', item: `https://www.meiri-dasai.com/category/${article.categorySlug}` }, { '@type': 'ListItem', position: 3, name: article.title }] }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-4 px-4 md:px-6 bg-background-100">
          <div className="max-w-[960px] mx-auto">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category?.name || '赛事资讯', to: `/category/${article.categorySlug}` }, { label: article.title }]} />
          </div>
        </div>

        <main>
          <article className="py-8 md:py-10 px-4 md:px-6">
            <div className="max-w-[800px] mx-auto">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">{article.title}</h1>

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary-600">{article.author[0]}</span>
                  </div>
                  <span className="text-sm text-foreground-700">{article.author}</span>
                </div>
                <span className="text-foreground-300">·</span>
                <span className="text-sm text-foreground-500">{article.date}</span>
                <span className="text-foreground-300">·</span>
                <span className="text-sm text-foreground-500">{article.readTime}</span>
                <div className="flex flex-wrap gap-1.5">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">{tag}</span>
                  ))}
                </div>
              </div>

              <div className="h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-8">
                <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover" />
              </div>

              <div className="prose prose-sm max-w-none">
                <div className="text-sm text-foreground-700 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: article.contentHtml }}
                />
              </div>

              <div className="border-t border-background-200/70 mt-10 pt-6">
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span>作者：{article.author}</span>
                  <span>发布时间：{article.date}</span>
                  <span>分类：{category?.name || '赛事资讯'}</span>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-8 md:py-10 px-4 md:px-6 bg-background-100">
              <div className="max-w-[800px] mx-auto">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      onClick={() => window.scrollTo(0, 0)}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img src={ra.imageUrl} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2">{ra.title}</h3>
                        <p className="text-xs text-foreground-500 mt-2">{ra.date} · {ra.readTime}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}